import { useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { BlackCTA, EASE, Reveal, Section } from "./shared";

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export const Calculator = () => {
    const [customers, setCustomers] = useState(100);
    const [revenue, setRevenue] = useState(100000);
    const [ads, setAds] = useState(2);

    const METRICS = [
        { label: "No. of customers / month", value: customers, set: setCustomers, min: 10, max: 2000, step: 10, fmt: (v) => v.toLocaleString("en-IN"), testId: "calc-customers-slider" },
        { label: "Revenue / month", value: revenue, set: setRevenue, min: 20000, max: 1000000, step: 10000, fmt: inr, testId: "calc-revenue-slider" },
        { label: "High-performing ads / month", value: ads, set: setAds, min: 0, max: 20, step: 1, fmt: (v) => `${v}`, testId: "calc-ads-slider" },
    ];

    const mult = (m) => 3 + (m.value / m.max) * 7;

    return (
        <Section>
            <Reveal className="max-w-3xl">
                <h2 className="font-display text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-ink sm:text-5xl">
                    Tell us your current state of your business, and discover how much you can grow with Purple as your AI growth partner.
                </h2>
                <p className="mt-6 max-w-2xl text-base text-neutral-600 sm:text-lg">
                    Slide your numbers on the left. On the right — the same business, as a Purple Business.
                </p>
            </Reveal>

            <div className="mt-12 grid gap-6 lg:grid-cols-2">
                <Reveal className="space-y-8 rounded-[2rem] border border-neutral-200 bg-white p-7 sm:p-9">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-neutral-400">Your business today</p>
                    {METRICS.map((m) => (
                        <div key={m.label}>
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-bold text-ink">{m.label}</p>
                                <p className="font-display text-sm font-bold text-neutral-500">{m.fmt(m.value)}</p>
                            </div>
                            <div className="mt-3">
                                <Slider data-testid={m.testId} value={[m.value]} onValueChange={([v]) => m.set(v)} min={m.min} max={m.max} step={m.step} />
                            </div>
                        </div>
                    ))}
                    <p className="text-xs leading-relaxed text-neutral-400">
                        Illustrative goals to explore together — not a prediction of what Purple will deliver.
                    </p>
                </Reveal>

                <Reveal delay={0.12} className="rounded-[2rem] bg-ink p-7 text-white sm:p-9">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">Once you become a Purple Business</p>
                    <div className="mt-6 space-y-6">
                        {METRICS.map((m) => (
                            <div key={m.label} className="flex items-end justify-between gap-4 border-b border-white/10 pb-5 last:border-0 last:pb-0">
                                <div>
                                    <p className="text-sm text-white/60">{m.label}</p>
                                    <p className="mt-1 font-display text-2xl font-bold sm:text-3xl" data-testid={`calc-out-${m.testId}`}>
                                        {m.fmt(Math.round(m.value * mult(m)))}
                                    </p>
                                    <p className="mt-0.5 text-xs text-white/40">from {m.fmt(m.value)}</p>
                                </div>
                                <span className="shrink-0 rounded-full bg-white/10 px-3 py-1.5 text-xs font-bold">
                                    ×{mult(m).toFixed(1)}
                                </span>
                            </div>
                        ))}
                    </div>
                    <p className="mt-6 text-xs leading-relaxed text-white/50">
                        Every projection stays in the 3×–10× range depending on where you slide — your starting point,
                        offer, budget and execution decide the real outcome.
                    </p>
                </Reveal>
            </div>
        </Section>
    );
};

const PACKAGE_ROWS = [
    ["My Business", "Build business context, improve your presence and create your Purple page."],
    ["My Customer", "Understand personas, learn from feedback and improve customer journeys."],
    ["Planner", "Explore opportunities, offers, experiments and campaign plans."],
    ["Ads", "Prepare Google and Meta campaigns, create ads, review budgets and learn from performance."],
    ["Tools", "Make content, schedule supported tasks and review reports."],
    ["Expert support", "Speak with a performance marketer when you need another perspective."],
];

export const Pricing = () => (
    <Section id="pricing">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <Reveal>
                <h2 className="font-display text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-ink sm:text-5xl">
                    Your next chapter, starting from ₹2,000 a month.
                </h2>
                <p className="mt-6 max-w-lg text-base text-neutral-600 sm:text-lg">
                    Bring the work of growing your business into one place. Full plan details, credit allowances and
                    feature availability will be shared soon.
                </p>
                <div className="mt-9 rounded-[2rem] border-2 border-ink bg-white p-8 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.18)]" data-testid="pricing-card">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-neutral-500">Plans start at</p>
                    <p className="mt-2 font-display text-5xl font-bold tracking-[-0.02em] text-ink">
                        ₹2,000<span className="text-lg text-neutral-400"> / month</span>
                    </p>
                    <p className="mt-4 text-sm text-neutral-500">Full plan details coming soon.</p>
                    <div className="mt-7">
                        <BlackCTA testId="pricing-cta">Join the waitlist</BlackCTA>
                    </div>
                    <p className="mt-4 text-xs font-bold text-neutral-400">Get plan details when they’re ready.</p>
                </div>
            </Reveal>

            <div className="space-y-3 lg:pt-14">
                {PACKAGE_ROWS.map(([title, text], i) => (
                    <Reveal key={title} delay={i * 0.06} className="rounded-[1.5rem] border border-neutral-200 bg-white p-6 transition-colors hover:border-ink">
                        <p className="font-display text-lg font-bold text-ink">{title}</p>
                        <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{text}</p>
                    </Reveal>
                ))}
            </div>
        </div>
    </Section>
);

const TEAM = [
    ["Mayank Mishra", "/assets/team-bhuvanyu.png"],
    ["Animesh Pimparikar", "/assets/team-anagh.png"],
    ["Dinesh Reddy", "/assets/team-dinesh.png"],
    ["Anagh Bisht", "/assets/team-animesh.png"],
    ["Bhuvanyu Sharma", "/assets/team-mayank.png"],
];

export const Team = () => (
    <Section id="team">
        <Reveal className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-ink sm:text-5xl">
                The people behind Purple.
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-base text-neutral-600 sm:text-lg">
                Connect with an expert marketer on a video call. Look at your dashboard together, discuss what’s
                happening, and get another perspective on the next change worth making.
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-base font-bold text-ink sm:text-lg">
                Purple’s approach is being shaped with input from 10 performance marketers.
            </p>
        </Reveal>

        <Reveal delay={0.1} className="mx-auto mt-10 max-w-2xl">
            <div className="flex items-center gap-4 rounded-[1.5rem] border border-neutral-200 bg-white p-6" data-testid="meet-call-card">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-paper">
                    <img src="/assets/meet-logo.png" alt="Google Meet" className="h-7 w-7" />
                </span>
                <div>
                    <p className="font-display text-base font-bold text-ink">Video call with an expert marketer</p>
                    <p className="text-sm text-neutral-500">Your dashboard on screen, decisions made together.</p>
                </div>
            </div>
        </Reveal>

        <div className="mx-auto mt-10 grid max-w-5xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {TEAM.map(([name, photo], i) => (
                <Reveal key={name} delay={i * 0.07}>
                    <div
                        data-testid={`team-card-${i}`}
                        className="group h-full rounded-[1.5rem] border border-neutral-200 bg-white p-3 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                    >
                        <img
                            src={photo}
                            alt={name}
                            className="aspect-[4/5] w-full rounded-[1.1rem] object-cover"
                        />
                        <p className="px-2 pb-2 pt-4 text-center font-display text-base font-bold text-ink">{name}</p>
                    </div>
                </Reveal>
            ))}
        </div>
    </Section>
);

const FOUNDERS = [
    {
        name: ["Supreet", "Venkata"],
        role: "Founder.",
        focus: "Product, marketing and growth.",
        bio: "Led Trademarkia, a $14M ARR legal app for small businesses, and always wanted to build something bigger for small business owners.",
        img: "/assets/founder-b.png",
    },
    {
        name: ["Likhit", "Garimella"],
        role: "Co-Founder.",
        focus: "Tech, app development and AI.",
        bio: "Built Venmo (by PayPal), a go-to payments solution for small businesses, and believes small businesses shouldn’t be left behind in the AI wave.",
        img: "/assets/founder-a.png",
    },
];

export const Founders = () => (
    <Section id="founders">
        <Reveal className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-ink sm:text-5xl">
                We want more business owners to have a real chance at what comes next.
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-base text-neutral-600 sm:text-lg">
                There are owners with good businesses, ideas they want to try, and customers they could serve better.
                Too often, the next step asks for time, specialist skills or a team they don’t have. We’re building
                Purple to make more of that work accessible.
            </p>
        </Reveal>
        <div className="mx-auto mt-16 grid max-w-3xl gap-14 sm:grid-cols-2 sm:gap-10">
            {FOUNDERS.map((f, i) => (
                <Reveal key={f.name[0]} delay={i * 0.12}>
                    <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.35, ease: EASE }}>
                        <img
                            src={f.img}
                            alt={`${f.name[0]} ${f.name[1]}`}
                            className="h-auto w-56 sm:w-64"
                            data-testid={`founder-img-${i}`}
                        />
                    </motion.div>
                    <h3 className="mt-9 font-display text-4xl font-bold leading-[1.04] tracking-[-0.02em] text-ink sm:text-5xl">
                        <span className="block">{f.name[0]}</span>
                        <span className="block">{f.name[1]}</span>
                    </h3>
                    <p className="mt-5 font-display text-2xl font-bold tracking-[-0.02em] text-ink">{f.role}</p>
                    <p className="mt-7 font-display text-2xl font-bold leading-[1.15] tracking-[-0.02em] text-ink">{f.focus}</p>
                    <p className="mt-5 max-w-sm text-base leading-relaxed text-neutral-600">{f.bio}</p>
                </Reveal>
            ))}
        </div>
    </Section>
);
